/**
=========================================================
* Argon Dashboard 2 PRO MUI - v3.0.1
=========================================================

* Product Page: https://www.creative-tim.com/product/argon-dashboard-pro-mui
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// @mui material components
import { createTheme } from "@mui/material/styles";
// import Fade from "@mui/material/Fade";

// Argon Dashboard 2 PRO MUI base styles
import colors from "_assets/argon/theme/base/colors";
import breakpoints from "_assets/argon/theme/base/breakpoints";
import typography from "_assets/argon/theme/base/typography";
import boxShadows from "_assets/argon/theme/base/boxShadows";
import borders from "_assets/argon/theme/base/borders";
import globals from "_assets/argon/theme/base/globals";

// Argon Dashboard 2 PRO MUI helper functions
import boxShadow from "_assets/argon/theme/functions/boxShadow";
import hexToRgb from "_assets/argon/theme/functions/hexToRgb";
import linearGradient from "_assets/argon/theme/functions/linearGradient";
import pxToRem from "_assets/argon/theme/functions/pxToRem";
import rgba from "_assets/argon/theme/functions/rgba";

// Argon Dashboard 2 PRO MUI components base styles for @mui material components
import sidenav from "_assets/argon/theme/components/sidenav";
import list from "_assets/argon/theme/components/list";
import listItem from "_assets/argon/theme/components/list/listItem";
import listItemText from "_assets/argon/theme/components/list/listItemText";
import card from "_assets/argon/theme/components/card";
import cardMedia from "_assets/argon/theme/components/card/cardMedia";
import cardContent from "_assets/argon/theme/components/card/cardContent";
import button from "_assets/argon/theme/components/button";
import iconButton from "_assets/argon/theme/components/iconButton";
import inputBase from "_assets/argon/theme/components/form/inputBase";
import menu from "_assets/argon/theme/components/menu";
import menuItem from "_assets/argon/theme/components/menu/menuItem";
import switchButton from "_assets/argon/theme/components/form/switchButton";
import divider from "_assets/argon/theme/components/divider";
import tableContainer from "_assets/argon/theme/components/table/tableContainer";
import tableHead from "_assets/argon/theme/components/table/tableHead";
import tableCell from "_assets/argon/theme/components/table/tableCell";
import linearProgress from "_assets/argon/theme/components/linearProgress";
import breadcrumbs from "_assets/argon/theme/components/breadcrumbs";
import slider from "_assets/argon/theme/components/slider";
import avatar from "_assets/argon/theme/components/avatar";
import tooltip from "_assets/argon/theme/components/tooltip";
import appBar from "_assets/argon/theme/components/appBar";
import tabs from "_assets/argon/theme/components/tabs";
import tab from "_assets/argon/theme/components/tabs/tab";
import stepper from "_assets/argon/theme/components/stepper";
import step from "_assets/argon/theme/components/stepper/step";
import stepConnector from "_assets/argon/theme/components/stepper/stepConnector";
import stepLabel from "_assets/argon/theme/components/stepper/stepLabel";
import stepIcon from "_assets/argon/theme/components/stepper/stepIcon";
import select from "_assets/argon/theme/components/form/select";
import formControlLabel from "_assets/argon/theme/components/form/formControlLabel";
import formLabel from "_assets/argon/theme/components/form/formLabel";
import checkbox from "_assets/argon/theme/components/form/checkbox";
import radio from "_assets/argon/theme/components/form/radio";
import autocomplete from "_assets/argon/theme/components/form/autocomplete";
import input from "_assets/argon/theme/components/form/input";
import flatpickr from "_assets/argon/theme/components/flatpickr";
import swal from "_assets/argon/theme/components/swal";
import container from "_assets/argon/theme/components/container";
import popover from "_assets/argon/theme/components/popover";
import buttonBase from "_assets/argon/theme/components/buttonBase";
import icon from "_assets/argon/theme/components/icon";
import svgIcon from "_assets/argon/theme/components/svgIcon";
import link from "_assets/argon/theme/components/link";
import dialog from "_assets/argon/theme/components/dialog";
import dialogTitle from "_assets/argon/theme/components/dialog/dialogTitle";
import dialogContent from "_assets/argon/theme/components/dialog/dialogContent";
import dialogContentText from "_assets/argon/theme/components/dialog/dialogContentText";
import dialogActions from "_assets/argon/theme/components/dialog/dialogActions";

export default createTheme({
  breakpoints: { ...breakpoints },
  palette: { ...colors },
  typography: { ...typography },
  boxShadows: { ...boxShadows },
  borders: { ...borders },
  functions: {
    boxShadow,
    hexToRgb,
    linearGradient,
    pxToRem,
    rgba,
  },

  components: {
    MuiCssBaseline: {
      styleOverrides: {
        ...globals,
        ...flatpickr,
        ...swal,
        ...container,
      },
    },
    MuiDrawer: { ...sidenav },
    MuiList: { ...list },
    MuiListItem: { ...listItem },
    MuiListItemText: { ...listItemText },
    MuiCard: { ...card },
    MuiCardMedia: { ...cardMedia },
    MuiCardContent: { ...cardContent },
    MuiButton: { ...button },
    MuiIconButton: { ...iconButton },
    MuiInputBase: { ...inputBase },
    MuiMenu: { ...menu },
    MuiMenuItem: { ...menuItem },
    MuiSwitch: { ...switchButton },
    MuiDivider: { ...divider },
    MuiTableContainer: { ...tableContainer },
    MuiTableHead: { ...tableHead },
    MuiTableCell: { ...tableCell },
    MuiLinearProgress: { ...linearProgress },
    MuiBreadcrumbs: { ...breadcrumbs },
    MuiSlider: { ...slider },
    MuiAvatar: { ...avatar },
    MuiTooltip: { ...tooltip },
    MuiAppBar: { ...appBar },
    MuiTabs: { ...tabs },
    MuiTab: { ...tab },
    MuiStepper: { ...stepper },
    MuiStep: { ...step },
    MuiStepConnector: { ...stepConnector },
    MuiStepLabel: { ...stepLabel },
    MuiStepIcon: { ...stepIcon },
    MuiSelect: { ...select },
    MuiFormControlLabel: { ...formControlLabel },
    MuiFormLabel: { ...formLabel },
    MuiCheckbox: { ...checkbox },
    MuiRadio: { ...radio },
    MuiAutocomplete: { ...autocomplete },
    MuiInput: { ...input },
    MuiOutlinedInput: { ...input },
    MuiFilledInput: { ...input },
    MuiPopover: { ...popover },
    MuiButtonBase: { ...buttonBase },
    MuiIcon: { ...icon },
    MuiSvgIcon: { ...svgIcon },
    MuiLink: { ...link },
    MuiDialog: { ...dialog },
    MuiDialogTitle: { ...dialogTitle },
    MuiDialogContent: { ...dialogContent },
    MuiDialogContentText: { ...dialogContentText },
    MuiDialogActions: { ...dialogActions },
  },
});
